import {
	formatNumberWithFormatOptions,
	getFieldValue,
} from '@supply-mover-2-0/core';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { cn } from '../../../utils/cn';
import type { DisplayComponentProps } from '../../types';
import { SIZE_MAP, evaluateColorRule } from '../../utils/colorRuleEngine';
import { LinkWrapper } from '../common/LinkWrapper';
import { handleTruncationTooltip } from './truncationTooltip';

export function NumberDisplay({
	field,
	data,
	value,
	listKey,
	objects,
	callbacks,
}: DisplayComponentProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	// Use the provided value or get from field config
	const rawValue =
		value !== undefined
			? value
			: field.dataConfig?.field
				? getFieldValue(data, field.dataConfig.field)
				: null;

	// Check if we should use default value instead of formatting null/undefined
	// Also check for other "empty" values that should use default
	const shouldUseDefault =
		(rawValue == null || rawValue === '') && !!field.dataConfig?.defaultValue;

	if (shouldUseDefault) {
		// Apply color rules for default value as well
		const colorValue = field.styleConfig?.colorRule
			? evaluateColorRule(field.styleConfig.colorRule, {
					...data,
					__resolvedValue: rawValue,
				})
			: null;

		const textColor =
			resolveThemeColor(colorValue?.text, isDark) ?? colors.values.textPrimary;

		const content = (
			<span
				className="truncate"
				style={{ color: textColor }}
				onMouseEnter={handleTruncationTooltip}
			>
				{t(field.dataConfig.defaultValue)}
			</span>
		);

		const isClickable = field.link?.enabled || false;
		const handlePress =
			field.link?.type === 'callback' &&
			field.link?.callbackName &&
			callbacks?.[field.link.callbackName]
				? () => callbacks[field.link.callbackName!](data)
				: undefined;

		return (
			<LinkWrapper
				isClickable={isClickable}
				onClick={handlePress}
				field={field}
				data={data}
				objects={objects}
			>
				{content}
			</LinkWrapper>
		);
	}

	// Format the number - force numberFormat to match display style (after spread so it can't be overridden)
	const formatOptions =
		field.displayStyle === 'currency'
			? {
					decimalPlaces: 2,
					currencySymbol: '$',
					...field.styleConfig?.formatOptions,
					numberFormat: 'currency' as const,
				}
			: field.displayStyle === 'percent'
				? {
						...field.styleConfig?.formatOptions,
						numberFormat: 'percent' as const,
					}
				: field.styleConfig?.formatOptions;

	const formattedValue = formatNumberWithFormatOptions(rawValue, formatOptions);

	// Determine text color based on rules or value
	let textColor = colors.values.textPrimary;

	// Compute the effective numeric value as the user sees it (post-formatting transforms).
	// For percentages with "Multiply by 100", the displayed value is rawValue * 100,
	// so range-based color rules should match against that, not the raw decimal.
	const numericRawValue =
		typeof rawValue === 'number'
			? rawValue
			: typeof rawValue === 'string'
				? parseFloat(rawValue)
				: null;
	let effectiveValue: unknown = numericRawValue;
	if (
		numericRawValue != null &&
		!isNaN(numericRawValue) &&
		formatOptions?.numberFormat === 'percent' &&
		formatOptions?.percentValueIsWholeNumber === false
	) {
		effectiveValue = numericRawValue * 100;
	}

	// Apply color rules if any using the correct color evaluation function
	const colorValue = field.styleConfig?.colorRule
		? evaluateColorRule(field.styleConfig.colorRule, {
				...data,
				__resolvedValue: effectiveValue,
			})
		: null;

	const resolvedTextColor = resolveThemeColor(colorValue?.text, isDark);

	if (resolvedTextColor != null) {
		textColor = resolvedTextColor;
	} else if (
		(formatOptions?.numberFormat === 'currency' ||
			field.displayStyle === 'currency') &&
		typeof rawValue === 'number' &&
		field.styleConfig?.colorRule?.type !== 'default'
	) {
		// Default color behavior for currency: red for negative, green for positive
		// Only apply if colorRule is not explicitly set to 'default'
		if (rawValue < 0) {
			textColor = colors.values.statusError;
		} else if (rawValue > 0 && formatOptions?.showPositiveColor) {
			textColor = colors.values.statusSuccess;
		}
	}

	// Get size configuration - fallback to 'md' if size key is not found in SIZE_MAP
	const sizeConfig = SIZE_MAP[field.styleConfig?.size ?? 'md'] ?? SIZE_MAP.md;

	const textAlign = formatOptions?.textAlign || 'left';
	const textAlignClass =
		{
			left: 'text-left',
			right: 'text-right',
			center: 'text-center',
		}[textAlign] || 'text-left';

	const fontWeight = formatOptions?.fontWeight || 'normal';
	const fontWeightClass =
		{
			normal: 'font-normal',
			bold: 'font-bold',
			semibold: 'font-semibold',
			medium: 'font-medium',
		}[fontWeight] || 'font-normal';

	// Fallback to default value if formatted value is empty/null and we have a default
	const displayValue =
		(formattedValue == null ||
			formattedValue === '' ||
			formattedValue === '-') &&
		field.dataConfig?.defaultValue
			? field.dataConfig.defaultValue
			: formattedValue;

	const content = (
		<span
			className={cn(textAlignClass, fontWeightClass, 'truncate')}
			style={{ color: textColor, fontSize: sizeConfig.fontSize }}
			onMouseEnter={handleTruncationTooltip}
		>
			{t(displayValue || '')}
		</span>
	);

	const isClickable = field.link?.enabled || false;
	const handlePress =
		field.link?.type === 'callback' &&
		field.link?.callbackName &&
		callbacks?.[field.link.callbackName]
			? () => callbacks[field.link.callbackName!](data)
			: undefined;

	return (
		<LinkWrapper
			isClickable={isClickable}
			onClick={handlePress}
			field={field}
			data={data}
			objects={objects}
		>
			{content}
		</LinkWrapper>
	);
}
